/**
 * One organization's row on the feature rollout panel (LOCKER-408 DoD3, DoD4,
 * DoD6).
 *
 * Four resolved states are visually distinct, because they are four different
 * facts about the rollout even though two pairs grant the same access:
 *
 *   On            — an activated assignment. Someone decided yes.
 *   Off           — a deactivated assignment. Someone decided no, and since
 *                   LOCKER-408 that decision is authoritative.
 *   On via default— no assignment; the feature's default grants it. Toggling
 *                   writes an explicit assignment, which is why it is NOT
 *                   locked: an explicit off now wins.
 *   Never set     — no assignment, default off. Nobody has decided.
 *
 * Colours follow the design canvas: primary #234285 for a decision made here,
 * secondary #94D2C2 for one inherited from the feature default.
 */
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material"
import moment from "moment"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { FeatureAccessState } from "shared/featureResolution"
import { Sl2Btn } from "../Sl2Btn"
import { FeatureRolloutRow as RowModel } from "./featureRolloutModel"
import { FeatureWriteFailure } from "./useFeatureAssignmentWrites"

/** Per-state presentation: the Source badge and the switch colour. */
const SOURCE_STYLES: Record<
  FeatureAccessState,
  { labelKey: string; bg: string; fg: string; track?: string }
> = {
  [FeatureAccessState.ActivatedByAssignment]: {
    labelKey: "feature.sourceAssignment",
    bg: "rgba(35, 66, 133, 0.1)",
    fg: "#234285",
  },
  [FeatureAccessState.DeactivatedByAssignment]: {
    labelKey: "feature.sourceAssignment",
    bg: "rgba(0, 0, 0, 0.06)",
    fg: "rgba(0, 0, 0, 0.6)",
  },
  [FeatureAccessState.ActivatedByDefault]: {
    labelKey: "feature.sourceFeatureDefault",
    bg: "rgba(148, 210, 194, 0.35)",
    fg: "#0f5f52",
  },
  [FeatureAccessState.Unassigned]: {
    labelKey: "feature.sourceNeverSet",
    bg: "rgba(0, 0, 0, 0.04)",
    fg: "rgba(0, 0, 0, 0.45)",
  },
}

const STATE_LABEL_KEYS: Record<FeatureAccessState, string> = {
  [FeatureAccessState.ActivatedByAssignment]: "feature.stateOn",
  [FeatureAccessState.DeactivatedByAssignment]: "feature.stateOff",
  [FeatureAccessState.ActivatedByDefault]: "feature.stateOnViaDefault",
  [FeatureAccessState.Unassigned]: "feature.stateNeverSet",
}

type FeatureRolloutRowProps = {
  row: RowModel
  selected: boolean
  onSelect: (tenantId: string) => void
  onToggle: (row: RowModel) => void
  isPending: boolean
  failure?: FeatureWriteFailure
  onRetry: (failure: FeatureWriteFailure) => void
}

export const FeatureRolloutRowItem: FC<FeatureRolloutRowProps> = ({
  row,
  selected,
  onSelect,
  onToggle,
  isPending,
  failure,
  onRetry,
}) => {
  const { t } = useTranslation()
  const source = SOURCE_STYLES[row.state]
  const inheritedFromDefault =
    row.state === FeatureAccessState.ActivatedByDefault

  return (
    <Stack
      data-testid={`feature-rollout-row-${row.tenantId}`}
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 2,
        py: 1.25,
        borderBottom: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.05)",
        bgcolor: failure
          ? "rgba(211, 47, 47, 0.04)"
          : selected
            ? "rgba(35, 66, 133, 0.04)"
            : "transparent",
        "&:hover": { bgcolor: failure ? undefined : "rgba(35, 66, 133, 0.03)" },
      }}
    >
      <Checkbox
        size="small"
        checked={selected}
        onChange={() => onSelect(row.tenantId)}
        inputProps={
          {
            "aria-label": t("feature.selectOrganization", {
              name: row.name,
            }) as string,
            "data-testid": `feature-rollout-select-${row.tenantId}`,
          } as any
        }
        sx={{ p: 0.5 }}
      />

      <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {row.name}
        </Typography>
        {row.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: "block", lineHeight: 1.3 }}
          >
            {row.description}
          </Typography>
        )}
      </Stack>

      <Box sx={{ width: 150, flex: "0 0 auto" }}>
        <Chip
          size="small"
          label={t(source.labelKey) as string}
          sx={{
            bgcolor: source.bg,
            color: source.fg,
            fontWeight: 600,
            fontSize: "0.6875rem",
            height: 20,
            borderRadius: 1,
          }}
        />
      </Box>

      <Box sx={{ width: 190, flex: "0 0 auto" }}>
        {failure ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ErrorOutlineIcon sx={{ fontSize: 15, color: "error.main" }} />
            <Tooltip title={failure.message}>
              <Typography variant="caption" color="error" noWrap>
                {t("feature.writeFailed")}
              </Typography>
            </Tooltip>
          </Stack>
        ) : isPending ? (
          <Typography variant="caption" sx={{ color: "#234285" }}>
            {t("saving")}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.changedAt
              ? moment(row.changedAt).format("YYYY-MM-DD HH:mm")
              : inheritedFromDefault
                ? t("feature.inheritedFromFeature")
                : t("feature.neverSetMeta")}
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ width: 150, flex: "0 0 auto" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ textAlign: "right" }}
        >
          {t(STATE_LABEL_KEYS[row.state])}
        </Typography>
        {failure ? (
          <Sl2Btn
            size="small"
            variant="outlined"
            color="error"
            onClick={() => onRetry(failure)}
            data-testid={`feature-rollout-retry-${row.tenantId}`}
            sx={{ minWidth: 0, py: 0, px: 1, fontSize: "0.6875rem" }}
          >
            {t("retry")}
          </Sl2Btn>
        ) : isPending ? (
          <CircularProgress size={16} sx={{ color: "#234285", mx: 1 }} />
        ) : (
          <Switch
            size="small"
            checked={row.granted}
            onChange={() => onToggle(row)}
            inputProps={
              {
                "aria-label": t("feature.toggleOrganization", {
                  name: row.name,
                }) as string,
                "data-testid": `feature-rollout-toggle-${row.tenantId}`,
              } as any
            }
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: inheritedFromDefault ? "#94D2C2" : "#234285",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: inheritedFromDefault ? "#94D2C2" : "#234285",
              },
            }}
          />
        )}
      </Stack>
    </Stack>
  )
}

export default FeatureRolloutRowItem
